// Ikonka u Jména a Příjmení
import { LoginOutlined, SettingOutlined } from '@ant-design/icons';
import { Col, Layout, Row } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { CSSProperties, useContext, useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import Title from 'components/atoms/Title';
import { Context, defaultStoreState } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import LanguageSelect from 'components/molecules/LanguageSelect';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import logoutApi from 'services/logout';

import { EProfilyTyp } from 'enums/profilyTyp';
import routePathEnum from 'enums/routePathEnum';

import getLanguageObject from 'utils/getLanguageObject';

import DropdownMenu from './DropdownMenu';

dayjs.extend(utc);

const appMenuNameTitle: CSSProperties = {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 'normal',
    fontSize: '24px',
    marginTop: '0.5em',
    marginBottom: '0.5em'
};

interface AppMenuProps {
    setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}

function convertSeconds(milliseconds: number) {
    const seconds = Math.floor(milliseconds % 60);
    const minutes = Math.floor(milliseconds / 60);

    return [minutes.toString().padStart(2, '0'), seconds.toString().padStart(2, '0')].join(':');
}

let prevTimestamp: dayjs.Dayjs;

const AppMenu: React.FC<AppMenuProps> = () => {
    const { navigateWorkaround } = useNavigateWorkaround();
    const { setLevel } = useProfily();
    const { profily, aktivniFirma, language, setStoreState, logoutTimestamp, spravceSelectedCard, lei } =
        useContext(Context);
    const [pageName, setPageName] = useState<string>(getLanguageObject(language).MUJ_DEPOZITAR || ''); // Název stránky
    const [autoLogoutCounter, setAutoLogoutCounter] = useState(0);
    const [timer, setTimer] = useState<number>();
    const isDLTlogin = profily?.typprihlaseni === 'DLT';
    const isLeiLoginless = !!lei?.loginless || window.location.href.includes('lei-prehled-uctu');
    // nastaveni aktivni stranky. Vpripade ze by doslo k castemu rerenderovani (zatim nedochazi), pridat parametr nebo prepsat do useMemo()

    const getPageName = () =>
        aktivniFirma?.typ === EProfilyTyp.UCASTNIK
            ? getLanguageObject(language).UCASTNIK + ' ' + aktivniFirma.ic
            : aktivniFirma?.typ === EProfilyTyp.SPRAVCELEI
              ? 'SPRAVA_LEI'
              : aktivniFirma?.spolecnost
                ? aktivniFirma.spolecnost
                : isDLTlogin || isLeiLoginless || window.location.href.includes(routePathEnum.LEI_LOGINLESS)
                  ? ''
                  : 'MUJ_DEPOZITAR';

    useEffect(() => {
        setPageName(getPageName());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aktivniFirma, spravceSelectedCard]); // Pripadne odebrat

    useEffect(() => {
        if (logoutTimestamp && logoutTimestamp !== prevTimestamp) {
            prevTimestamp = logoutTimestamp;
            const SecondsTime = Math.floor(logoutTimestamp.diff(dayjs()) / 1000);
            setAutoLogoutCounter(SecondsTime);
            if (SecondsTime > 0) {
                setTimer(pv => {
                    window.clearInterval(pv);
                    return window.setInterval(() => {
                        setAutoLogoutCounter(logoutTimestamp.diff(dayjs()) / 1000);
                    }, 1000);
                });
            }
            return () => {
                if (timer) {
                    window.clearInterval(timer);
                }
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [logoutTimestamp]);

    return (
        <Layout.Header className={'app-menu-header'}>
            <Row>
                <Col
                    style={{
                        maxWidth: 120
                    }}
                    className="app-menu-logo"
                    onClick={() => {
                        setPageName(getPageName());
                    }}
                >
                    <AnchorComponent
                        link={profily?.typprihlaseni !== 'DLT' && !isLeiLoginless}
                        to={
                            !window.location.href.includes(routePathEnum.LOGIN_ROZCESTNIK) &&
                            aktivniFirma?.typ !== EProfilyTyp.JINAOSOBA
                                ? routePathEnum.MAIN_PAGE
                                : undefined
                        }
                        id="mujDepLogoBtn"
                        onClick={() => {
                            setLevel('lei', null);
                            window.location.href.includes(routePathEnum.LOGIN_ROZCESTNIK) &&
                                window.location.replace(
                                    window.location.href.includes('test')
                                        ? 'https://test.cdcp.cz'
                                        : 'https://www.cdcp.cz'
                                );
                        }}
                    >
                        <div
                            className={isDLTlogin ? 'menuLogoDlt' : 'menuLogo'}
                            style={
                                isDLTlogin
                                    ? {
                                          // width: 400,
                                          marginRight: '20px',
                                          position: 'relative',
                                          top: -10,
                                          left: -20,
                                          paddingRight: 10
                                      }
                                    : {
                                          maxWidth: 100,
                                          marginRight: '20px',
                                          position: 'relative',
                                          top: -6,
                                          left: -6,
                                          paddingRight: 10,
                                          borderRight: '3px solid #006866'
                                      }
                            }
                        />
                    </AnchorComponent>
                </Col>

                <Col
                    style={{
                        flexGrow: 1,
                        marginLeft: isDLTlogin ? 190 : 0
                    }}
                >
                    <Row>
                        <Title
                            text={
                                lei?.loginless?.typzadosti ? 'LEI_REGISTRACE_' + lei?.loginless?.typzadosti : pageName
                            }
                            level={1}
                            style={{
                                ...appMenuNameTitle,
                                ...(isLeiLoginless && {
                                    position: 'relative',
                                    margin: '0 auto',
                                    left: '-60px',
                                    top: 10
                                })
                            }}
                        />
                    </Row>
                </Col>

                {(window.location.hostname.includes('test') || window.location.hostname.includes('styrax')) && (
                    <p
                        style={{
                            position: 'absolute',
                            textAlign: 'center',
                            left: '48%',
                            marginTop: '20px',
                            color: '#006866',
                            fontSize: 40
                        }}
                    >
                        {window.location.hostname.includes('styrax') ? 'DEV' : 'TEST'}
                    </p>
                )}
                <Col
                    className="app-menu-user-section"
                    style={
                        isLeiLoginless
                            ? {
                                  position: 'absolute',
                                  right: 0
                              }
                            : {}
                    }
                >
                    {profily && (
                        <Row justify="center">
                            {!isLeiLoginless && (
                                <>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '50px',
                                            height: '50px',
                                            background:
                                                'linear-gradient(to right, rgba(0, 119, 119, 1), rgba(0, 119, 119, 0.8))',
                                            backgroundSize: 'cover',
                                            // boxShadow: '0 0 10px rgba(0, 119, 119, 0.5)',
                                            borderRadius: '50%',
                                            padding: '1px 2.5px 0px 0px'
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize: '24px',
                                                fontWeight: 'bold',
                                                color: '#fff',
                                                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
                                                position: 'relative',
                                                top: '-1px'
                                            }}
                                        >
                                            {`${profily?.jmeno.slice(0, 1).toUpperCase()}${profily?.prijmeni
                                                .slice(0, 1)
                                                .toUpperCase()}`}
                                        </span>
                                    </div>
                                    <DropdownMenu />
                                </>
                            )}

                            <p
                                style={{
                                    padding: '5px 0px',
                                    textAlign: 'center',
                                    margin: 0,
                                    marginLeft: 0,
                                    lineHeight: 1.35,
                                    fontSize: 14,
                                    fontWeight: 600
                                }}
                            >
                                <AnchorComponent
                                    // link={true}
                                    style={{
                                        display: 'block',
                                        position: 'relative',
                                        marginTop: 'auto',
                                        marginBottom: 'auto',
                                        marginRight: 26
                                    }}
                                >
                                    <LanguageSelect />
                                    <Row
                                        style={{
                                            position: 'relative',
                                            top: -6.5
                                        }}
                                    >
                                        <TranslateText>LANG_SELECT</TranslateText>
                                    </Row>
                                </AnchorComponent>
                            </p>

                            {!isLeiLoginless && (
                                <>
                                    <p
                                        style={{
                                            padding: '5px 0px',
                                            textAlign: 'center',
                                            marginTop: 0,
                                            marginLeft: 0,
                                            lineHeight: 1.35,
                                            fontSize: 14,
                                            fontWeight: 600
                                        }}
                                    >
                                        <AnchorComponent
                                            link={true}
                                            to={routePathEnum.MUJ_PROFIL}
                                            style={{
                                                display: 'block',
                                                position: 'relative',
                                                marginTop: 'auto',
                                                marginBottom: 'auto',
                                                marginRight: 26
                                            }}
                                        >
                                            <SettingOutlined
                                                style={{
                                                    fontSize: '25px'
                                                }}
                                            />
                                            <Row>
                                                <TranslateText>NASTAVENI</TranslateText>
                                            </Row>
                                        </AnchorComponent>
                                    </p>
                                    <div
                                        style={{
                                            padding: '5px 0px',
                                            textAlign: 'center',
                                            marginTop: 0,
                                            marginRight: 10,
                                            lineHeight: 1.35,
                                            fontSize: 14,
                                            fontWeight: 400
                                        }}
                                    >
                                        <div>
                                            <TranslateText>ODHLASIT_PRO_NEAKTIVITU</TranslateText>
                                        </div>
                                        <div
                                            style={{
                                                fontWeight: 600
                                            }}
                                        >
                                            {autoLogoutCounter > 0 ? convertSeconds(autoLogoutCounter) : '00:00'}
                                        </div>
                                    </div>
                                    <p
                                        style={{
                                            padding: '5px 0px',
                                            textAlign: 'center',
                                            margin: 0,
                                            marginRight: 24,
                                            lineHeight: 1.35,
                                            fontSize: 14,
                                            fontWeight: 600
                                        }}
                                    >
                                        <AnchorComponent
                                            onClick={() => {
                                                logoutApi();
                                                isDLTlogin
                                                    ? window.location.replace(
                                                          window.location.origin.includes('styrax')
                                                              ? 'https://dlt-dev.styrax.cz/'
                                                              : window.location.origin.includes('test')
                                                                ? 'https://test-dlt.cdcp.cz/'
                                                                : 'https://dlt.cdcp.cz/'
                                                      )
                                                    : navigateWorkaround(routePathEnum.LOGOUT);

                                                setStoreState &&
                                                    setStoreState({
                                                        ...defaultStoreState,
                                                        language: language
                                                    });
                                            }}
                                            id="odhlasitBtn"
                                        >
                                            <foreignObject x="5" y="5" width="25" height="25">
                                                <LoginOutlined
                                                    style={{
                                                        fontSize: '25px'
                                                    }}
                                                />
                                            </foreignObject>
                                            <Row>
                                                <TranslateText>ODHLASIT</TranslateText>
                                            </Row>
                                        </AnchorComponent>
                                    </p>
                                </>
                            )}
                        </Row>
                    )}
                    <Col
                        style={{
                            position: 'relative',
                            top: 10,
                            right: 16
                        }}
                    >
                        {(window.location.href.includes(routePathEnum.LOGIN_ROZCESTNIK) ||
                            window.location.href.includes('lei-cdcp')) && <LanguageSelect />}
                    </Col>
                </Col>
            </Row>
        </Layout.Header>
    );
};

export default AppMenu;
